import * as React from "react";
import { RefreshCw, Download, X, ZoomIn, Upload } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent } from "../ui/Card";
import { Checkbox } from "../ui/Checkbox";
import { Input } from "../ui/Input";
import { useSpaceStore } from "../../stores/spaceStore";
import { getShading, regenerateShading, getMask, uploadShading } from "../../services/api";
import { useToast } from "../ui/Toast";
import { downloadBlob, base64ToImageUrl, parseSurfaceLabel, formatSurfaceLabelFromRaw } from "../../lib/utils";

interface SurfaceBlurConfig {
  useCustom: boolean;
  blurEnabled: boolean;
  blurRadius: number;
}

async function canvasToBlobAsync(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas toBlob failed"));
    }, "image/png");
  });
}

async function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
}

async function compositeShading(
  shadingLayers: Map<string, ImageBitmap>,
  masks: Array<{ label: string; image: HTMLImageElement }>,
  surfaceToBlurKey: Map<string, string>,
  defaultBlurKey: string
): Promise<Blob> {
  const firstLayer = shadingLayers.values().next().value;
  if (!firstLayer) throw new Error("No shading layers");
  const width = firstLayer.width;
  const height = firstLayer.height;

  const outCanvas = document.createElement("canvas");
  outCanvas.width = width;
  outCanvas.height = height;
  const outCtx = outCanvas.getContext("2d")!;

  // Paint default layer as base
  const defaultLayer = shadingLayers.get(defaultBlurKey);
  if (defaultLayer) {
    outCtx.drawImage(defaultLayer, 0, 0);
  }

  // If only one unique group, no compositing needed
  if (shadingLayers.size <= 1) {
    return canvasToBlobAsync(outCanvas);
  }

  const outImageData = outCtx.getImageData(0, 0, width, height);
  const outPixels = outImageData.data;

  // Pre-render each non-default layer to get pixel data
  const layerPixelData = new Map<string, Uint8ClampedArray>();
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = width;
  tempCanvas.height = height;
  const tempCtx = tempCanvas.getContext("2d")!;

  for (const [key, bitmap] of shadingLayers) {
    if (key === defaultBlurKey) continue;
    tempCtx.clearRect(0, 0, width, height);
    tempCtx.drawImage(bitmap, 0, 0);
    const data = tempCtx.getImageData(0, 0, width, height);
    layerPixelData.set(key, data.data);
  }

  // For each mask, blend the corresponding non-default shading layer
  for (const maskInfo of masks) {
    const blurKey = surfaceToBlurKey.get(maskInfo.label);
    if (!blurKey || blurKey === defaultBlurKey) continue;

    const layerData = layerPixelData.get(blurKey);
    if (!layerData) continue;

    tempCtx.clearRect(0, 0, width, height);
    tempCtx.drawImage(maskInfo.image, 0, 0, width, height);
    const maskData = tempCtx.getImageData(0, 0, width, height).data;

    for (let i = 0; i < width * height; i++) {
      const pi = i * 4;
      // Average R/G/B to mitigate JPEG chroma subsampling artifacts
      let maskAlpha = (maskData[pi] + maskData[pi + 1] + maskData[pi + 2]) / (3 * 255);
      // Threshold to reduce JPEG compression edge noise
      if (maskAlpha < 0.05) maskAlpha = 0;
      else if (maskAlpha > 0.95) maskAlpha = 1;

      if (maskAlpha > 0) {
        outPixels[pi] = Math.round(outPixels[pi] * (1 - maskAlpha) + layerData[pi] * maskAlpha);
        outPixels[pi + 1] = Math.round(outPixels[pi + 1] * (1 - maskAlpha) + layerData[pi + 1] * maskAlpha);
        outPixels[pi + 2] = Math.round(outPixels[pi + 2] * (1 - maskAlpha) + layerData[pi + 2] * maskAlpha);
      }
    }
  }

  outCtx.putImageData(outImageData, 0, 0);
  return canvasToBlobAsync(outCanvas);
}

export function ShadingManager() {
  const [shadingImage, setShadingImage] = React.useState<string | null>(null);
  const [shadingBlob, setShadingBlob] = React.useState<Blob | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRegenerating, setIsRegenerating] = React.useState(false);
  const [showShadingDialog, setShowShadingDialog] = React.useState(false);
  // Defaults match the analysis-worker pipeline: light blur (2.0) + thin-structure
  // removal replaced the old heavy-blur (30) workaround for texture-pattern leakage.
  const [blurEnabled, setBlurEnabled] = React.useState(true);
  const [blurRadius, setBlurRadius] = React.useState(2.0);
  const [removeThinStructures, setRemoveThinStructures] = React.useState(true);

  // Per-surface blur
  const [showPerSurface, setShowPerSurface] = React.useState(false);
  const [surfaceConfigs, setSurfaceConfigs] = React.useState<Record<string, SurfaceBlurConfig>>({});
  const [progressMsg, setProgressMsg] = React.useState("");

  // Upload external shading
  const uploadInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const { spaceId, originalImage, surfaces } = useSpaceStore();
  const { addToast } = useToast();

  // Initialize per-surface configs when surfaces change
  React.useEffect(() => {
    const configs: Record<string, SurfaceBlurConfig> = {};
    for (const s of surfaces) {
      configs[s.label] = surfaceConfigs[s.label] || {
        useCustom: false,
        blurEnabled: true,
        blurRadius: 2.0,
      };
    }
    setSurfaceConfigs(configs);
  }, [surfaces]);

  const updateSurfaceConfig = (label: string, patch: Partial<SurfaceBlurConfig>) => {
    setSurfaceConfigs((prev) => ({
      ...prev,
      [label]: { ...prev[label], ...patch },
    }));
  };

  const handleLoadShading = async () => {
    setIsLoading(true);
    try {
      const blob = await getShading(spaceId || undefined);
      setShadingBlob(blob);
      const url = URL.createObjectURL(blob);
      setShadingImage(url);
      addToast("success", "Shading loaded");
    } catch (error) {
      addToast("error", "Failed to load shading");
    } finally {
      setIsLoading(false);
    }
  };

  const getEffectiveBlur = (label: string): { enabled: boolean; radius: number } => {
    const cfg = surfaceConfigs[label];
    if (cfg && cfg.useCustom) {
      return { enabled: cfg.blurEnabled, radius: cfg.blurRadius };
    }
    return { enabled: blurEnabled, radius: blurRadius };
  };

  const hasAnyCustomOverrides = (): boolean => {
    return Object.values(surfaceConfigs).some((c) => c.useCustom);
  };

  const handleRegenerate = async () => {
    if (showPerSurface && hasAnyCustomOverrides()) {
      await handleRegeneratePerSurface();
    } else {
      await handleRegenerateSimple();
    }
  };

  const handleRegenerateSimple = async () => {
    setIsRegenerating(true);
    try {
      const response = await regenerateShading(spaceId || undefined, blurEnabled, blurRadius, removeThinStructures);
      if (response.status === "SUCCESS") {
        addToast("success", "Shading regenerated successfully");
        await handleLoadShading();
      } else {
        throw new Error(response.message || "Failed to regenerate shading");
      }
    } catch (error) {
      addToast("error", "Failed to regenerate shading");
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleRegeneratePerSurface = async () => {
    setIsRegenerating(true);
    setProgressMsg("Grouping surfaces...");

    try {
      // Group surfaces by effective blur key
      const groups = new Map<string, string[]>();
      const surfaceToKey = new Map<string, string>();

      for (const s of surfaces) {
        const { enabled, radius } = getEffectiveBlur(s.label);
        const key = enabled ? `true:${radius}` : "false:0";
        surfaceToKey.set(s.label, key);
        const arr = groups.get(key) || [];
        arr.push(s.label);
        groups.set(key, arr);
      }

      // Default key = global setting
      const defaultKey = blurEnabled ? `true:${blurRadius}` : "false:0";

      // Generate shading layers (sequential - each overwrites server state)
      const shadingLayers = new Map<string, ImageBitmap>();
      let step = 0;
      const totalSteps = groups.size;

      for (const [blurKey] of groups) {
        step++;
        const [enabledStr, radiusStr] = blurKey.split(":");
        const enabled = enabledStr === "true";
        const radius = parseFloat(radiusStr);

        setProgressMsg(`Generating shading ${step}/${totalSteps} (blur=${enabled ? radius : "off"})...`);

        const res = await regenerateShading(spaceId || undefined, enabled, radius, removeThinStructures);
        if (res.status !== "SUCCESS") {
          throw new Error(res.message || `Failed for blur=${enabled ? radius : "off"}`);
        }

        const blob = await getShading(spaceId || undefined);
        const bitmap = await createImageBitmap(blob);
        shadingLayers.set(blurKey, bitmap);
      }

      // If only one group, skip mask loading + compositing
      if (groups.size === 1) {
        const onlyKey = groups.keys().next().value!;
        const bitmap = shadingLayers.get(onlyKey)!;
        // Convert bitmap to blob
        const c = document.createElement("canvas");
        c.width = bitmap.width;
        c.height = bitmap.height;
        c.getContext("2d")!.drawImage(bitmap, 0, 0);
        const blob = await canvasToBlobAsync(c);
        setShadingBlob(blob);
        setShadingImage(URL.createObjectURL(blob));
        // Sync to server so mapping uses this shading
        setProgressMsg("Syncing to server...");
        await syncShadingToServer(blob);
        addToast("success", "Shading regenerated");
        return;
      }

      // Load masks in parallel
      setProgressMsg("Loading surface masks...");
      const maskPromises = surfaces.map(async (s) => {
        const { type, index } = parseSurfaceLabel(s.label);
        const response = await getMask(type, index, spaceId || undefined);
        if (response.status !== "SUCCESS" || !response.output) {
          throw new Error(`Failed to load mask for ${s.label}`);
        }
        const url = base64ToImageUrl(response.output, "image/jpeg");
        const img = await loadImageFromUrl(url);
        return { label: s.label, image: img };
      });
      const masks = await Promise.all(maskPromises);

      // Composite
      setProgressMsg("Compositing...");
      const blob = await compositeShading(shadingLayers, masks, surfaceToKey, defaultKey);
      setShadingBlob(blob);
      setShadingImage(URL.createObjectURL(blob));
      // Sync composited result to server so mapping uses this shading
      setProgressMsg("Syncing to server...");
      await syncShadingToServer(blob);
      addToast("success", `Per-surface shading generated (${groups.size} layers)`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed";
      addToast("error", msg);
    } finally {
      setIsRegenerating(false);
      setProgressMsg("");
    }
  };

  const handleDownload = () => {
    if (shadingBlob) {
      downloadBlob(shadingBlob, "shading.png");
    }
  };

  const syncShadingToServer = async (blob: Blob): Promise<boolean> => {
    try {
      const response = await uploadShading(blob, spaceId || undefined);
      if (response.status !== "SUCCESS") {
        console.warn("Failed to sync shading to server:", response.message);
        return false;
      }
      return true;
    } catch (error) {
      console.warn("Failed to sync shading to server:", error);
      return false;
    }
  };

  const handleUploadShading = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !spaceId) return;

    setIsUploading(true);
    try {
      const response = await uploadShading(file, spaceId);
      if (response.status === "SUCCESS") {
        addToast("success", "Shading uploaded successfully");
        await handleLoadShading();
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to upload shading";
      addToast("error", msg);
    } finally {
      setIsUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    }
  };

  if (!spaceId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Load a space first to manage shading
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={handleLoadShading}
          loading={isLoading}
        >
          <Download className="h-4 w-4 mr-1" />
          Load
        </Button>
        <Button
          size="sm"
          onClick={handleRegenerate}
          loading={isRegenerating}
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Regenerate
        </Button>
        {shadingImage && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
        )}
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={handleUploadShading}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => uploadInputRef.current?.click()}
          loading={isUploading}
        >
          <Upload className="h-4 w-4 mr-1" />
          Upload
        </Button>
        <div className="flex items-center gap-2 ml-2">
          <Checkbox
            checked={blurEnabled}
            onCheckedChange={(checked) => setBlurEnabled(checked === true)}
            label="Blur"
          />
          <Input
            type="number"
            value={blurRadius}
            onChange={(e) => setBlurRadius(Number(e.target.value))}
            disabled={!blurEnabled}
            className="w-20 h-7 text-xs"
            min={0}
            step={0.1}
          />
        </div>
        <div className="flex items-center gap-2 ml-2" title="Remove texture-pattern leakage (grout lines, plank joints) from the shading while keeping contact shadows">
          <Checkbox
            checked={removeThinStructures}
            onCheckedChange={(checked) => setRemoveThinStructures(checked === true)}
            label="Remove patterns"
          />
        </div>
        <div className="flex items-center gap-2 ml-2">
          <Checkbox
            checked={showPerSurface}
            onCheckedChange={(checked) => setShowPerSurface(checked === true)}
            label="Per-Surface"
          />
        </div>
        {isRegenerating && progressMsg && (
          <span className="text-xs text-primary font-medium ml-2">{progressMsg}</span>
        )}
        {!isRegenerating && (
          <span className="text-xs text-muted-foreground ml-2">
            Regenerating may take a few minutes
          </span>
        )}
      </div>

      {/* Per-surface controls */}
      {showPerSurface && surfaces.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Per-Surface Blur Overrides
          </p>
          <div className="space-y-1.5">
            {surfaces.map((s) => {
              const cfg = surfaceConfigs[s.label];
              if (!cfg) return null;
              return (
                <div key={s.label} className="flex items-center gap-3 text-xs">
                  <span className="w-16 font-medium truncate">{formatSurfaceLabelFromRaw(s.label)}</span>
                  <Checkbox
                    checked={cfg.useCustom}
                    onCheckedChange={(checked) =>
                      updateSurfaceConfig(s.label, { useCustom: checked === true })
                    }
                    label="Custom"
                  />
                  {cfg.useCustom ? (
                    <>
                      <Checkbox
                        checked={cfg.blurEnabled}
                        onCheckedChange={(checked) =>
                          updateSurfaceConfig(s.label, { blurEnabled: checked === true })
                        }
                        label="Blur"
                      />
                      <Input
                        type="number"
                        value={cfg.blurRadius}
                        onChange={(e) =>
                          updateSurfaceConfig(s.label, { blurRadius: Number(e.target.value) })
                        }
                        disabled={!cfg.blurEnabled}
                        className="w-16 h-6 text-xs"
                        min={0}
                        step={0.1}
                      />
                    </>
                  ) : (
                    <span className="text-muted-foreground">
                      default ({blurEnabled ? blurRadius : "off"})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Preview - side by side */}
      <div className="grid grid-cols-2 gap-3">
        {/* Original */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Original</p>
          <div className="aspect-video bg-muted rounded overflow-hidden border">
            {originalImage ? (
              <img src={originalImage} alt="Original" className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                No image
              </div>
            )}
          </div>
        </div>

        {/* Shading */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Shading Layer</p>
          {shadingImage ? (
            <button
              onClick={() => setShowShadingDialog(true)}
              className="aspect-video w-full bg-muted rounded overflow-hidden border hover:ring-2 hover:ring-primary/50 transition-all group relative"
            >
              <img src={shadingImage} alt="Shading" className="w-full h-full object-contain" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ) : (
            <div className="aspect-video bg-muted rounded overflow-hidden border flex items-center justify-center text-xs text-muted-foreground">
              Click "Load" to view
            </div>
          )}
        </div>
      </div>

      {/* Shading Image Dialog */}
      {showShadingDialog && shadingImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setShowShadingDialog(false)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img
              src={shadingImage}
              alt="Shading full"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowShadingDialog(false)}
              className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
